import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Search, Edit2, Trash2, ChevronRight, Save, X, Eye, EyeOff, LayoutGrid, ExternalLink, Tag, Link2, Bold, Italic, Underline, List, Image as ImageIcon, Upload, Trash } from "lucide-react";
import CategoryTreeNode from "./CategoryTreeNode";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getToken = () => localStorage.getItem("it_token");

function slugify(s) {
  return s.toLowerCase().replace(/&/g, "and").replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
}

// Upload file to object storage
async function uploadFile(file) {
  const fd = new FormData();
  fd.append("file", file);
  const res = await axios.post(`${API}/upload/image`, fd, {
    headers: { Authorization: `Bearer ${getToken()}`, "Content-Type": "multipart/form-data" },
  });
  return res.data.url;
}

// Simple image upload widget
function ImageUploader({ value, onChange, label, hint, aspectClass = "aspect-square" }) {
  const inputRef = useRef();
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadFile(file);
      onChange(url);
      toast.success(`${label} uploaded`);
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  return (
    <div>
      <p className="text-sm font-semibold text-slate-700 mb-2">{label}</p>
      <div className="flex gap-3 items-start">
        {value ? (
          <div className={`w-28 flex-shrink-0 rounded-xl overflow-hidden border border-slate-200 bg-slate-50 ${aspectClass}`}>
            <img src={value} alt="" className="w-full h-full object-cover" />
          </div>
        ) : (
          <div className={`w-28 flex-shrink-0 rounded-xl border-2 border-dashed border-slate-200 bg-slate-50 flex items-center justify-center ${aspectClass}`}>
            <ImageIcon className="w-6 h-6 text-slate-300" />
          </div>
        )}
        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="px-4 py-2 rounded-lg border-2 border-blue-500 text-blue-600 text-sm font-semibold hover:bg-blue-50 transition-colors disabled:opacity-50"
          >
            {uploading ? "Uploading..." : (label.includes("Banner") ? "Upload Banner" : "Upload Image")}
          </button>
          {value && (
            <button
              type="button"
              onClick={() => onChange("")}
              className="px-4 py-2 rounded-lg border-2 border-red-400 text-red-500 text-sm font-semibold hover:bg-red-50 transition-colors"
            >
              Remove
            </button>
          )}
          {hint && <p className="text-xs text-slate-400">{hint}</p>}
          <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={handleFile} />
        </div>
      </div>
    </div>
  );
}

// Minimal rich-text toolbar + contentEditable
function RichTextEditor({ value, onChange }) {
  const editorRef = useRef();
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (editorRef.current && !initialized) {
      editorRef.current.innerHTML = value || "";
      setInitialized(true);
    }
  }, [value, initialized]);

  const exec = (cmd, val = null) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, val);
  };

  const TOOLBAR = [
    { icon: Bold, cmd: "bold", title: "Bold" },
    { icon: Italic, cmd: "italic", title: "Italic" },
    { icon: Underline, cmd: "underline", title: "Underline" },
    { icon: List, cmd: "insertUnorderedList", title: "Bullet list" },
    { icon: Link2, cmd: "createLink", title: "Link", needsVal: true },
  ];

  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden focus-within:border-blue-400 transition-colors">
      <div className="flex items-center gap-0.5 px-2 py-1.5 border-b border-slate-100 bg-slate-50">
        {TOOLBAR.map(({ icon: Icon, cmd, title, needsVal }) => (
          <button
            key={cmd}
            type="button"
            title={title}
            onMouseDown={e => { e.preventDefault(); if (needsVal) { const v = prompt("Enter URL:"); if (v) exec(cmd, v); } else exec(cmd); }}
            className="p-1.5 rounded hover:bg-slate-200 text-slate-600 transition-colors"
          >
            <Icon className="w-3.5 h-3.5" />
          </button>
        ))}
      </div>
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={() => onChange(editorRef.current?.innerHTML || "")}
        className="min-h-[90px] p-3 text-sm text-slate-700 outline-none"
        data-placeholder="Wide range of products..."
        style={{ wordBreak: "break-word" }}
      />
    </div>
  );
}

function CatPanel({ cat, allCats, onClose, onSaved }) {
  const isEdit = !!cat;
  const [tab, setTab] = useState("Basic Details");
  const [form, setForm] = useState({
    name: cat?.name || cat?.title || "",
    slug: cat?.slug || "",
    parent_slug: cat?.parent_slug || "",
    description: cat?.description || "",
    thumbnail_image: cat?.thumbnail_image || "",
    banner_image: cat?.banner_image || "",
    bg_color: cat?.bg_color || "#0f172a",
    status: cat?.status || "active",
    position: cat?.position ?? 1,
    seo_title: cat?.seo_title || "",
    seo_description: cat?.seo_description || "",
  });
  const [saving, setSaving] = useState(false);
  const u = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error("Category name is required"); return; }
    setSaving(true);
    try {
      const payload = { ...form, slug: form.slug || slugify(form.name) };
      if (isEdit) {
        await axios.put(`${API}/admin/category/${cat.slug}`, payload, { headers: { Authorization: `Bearer ${getToken()}` } });
        toast.success("Category updated");
      } else {
        await axios.post(`${API}/admin/categories`, payload, { headers: { Authorization: `Bearer ${getToken()}` } });
        toast.success("Category created");
      }
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const TABS = ["Basic Details", "SEO", "Display", "Banner & Media"];

  return (
    <div className="bg-white border-l border-slate-200 w-[400px] flex-shrink-0 flex flex-col" data-testid="cat-panel">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
        <h3 className="text-base font-bold text-slate-900">{isEdit ? "Edit Category" : "Add / Edit Category"}</h3>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-600 p-1 rounded-lg hover:bg-slate-100 transition-colors">
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-slate-100 overflow-x-auto px-2">
        {TABS.map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`text-sm font-medium px-3 py-3 border-b-2 whitespace-nowrap transition-colors ${
              tab === t ? "border-blue-600 text-blue-600" : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-5 space-y-5">

        {tab === "Basic Details" && (
          <>
            {/* Category Name */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                Category Name <span className="text-red-500">*</span>
              </label>
              <input
                value={form.name}
                onChange={e => { u("name", e.target.value); if (!isEdit) u("slug", slugify(e.target.value)); }}
                placeholder="Mobile Accessories"
                className="w-full text-sm border border-slate-200 rounded-xl px-3 py-2.5 focus:outline-none focus:border-blue-400 bg-white"
              />
            </div>

            {/* Parent Category */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                Parent Category <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <select
                  value={form.parent_slug}
                  onChange={e => u("parent_slug", e.target.value)}
                  className="w-full text-sm border border-slate-200 rounded-xl px-3 py-2.5 focus:outline-none focus:border-blue-400 bg-white appearance-none pr-8"
                >
                  <option value="">None (Top Level)</option>
                  {allCats.filter(c => c.slug !== cat?.slug).map(c => (
                    <option key={c.slug} value={c.slug}>{c.name || c.title}</option>
                  ))}
                </select>
                <ChevronRight className="absolute right-3 top-1/2 -translate-y-1/2 rotate-90 w-4 h-4 text-slate-400 pointer-events-none" />
              </div>
            </div>

            {/* URL Slug */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                URL Slug <span className="text-red-500">*</span>
              </label>
              <div className="flex items-center border border-slate-200 rounded-xl focus-within:border-blue-400 overflow-hidden">
                <input
                  value={form.slug}
                  onChange={e => u("slug", slugify(e.target.value))}
                  placeholder="mobile-accessories"
                  className="flex-1 text-sm px-3 py-2.5 outline-none bg-white"
                />
                <div className="border-l border-slate-200 px-3 py-2.5 bg-slate-50">
                  <Link2 className="w-4 h-4 text-slate-400" />
                </div>
              </div>
              {form.slug && (
                <p className="text-xs text-slate-400 mt-1.5 flex items-center gap-1">
                  <span>Preview URL:</span>
                  <a
                    href={`/c/${form.slug}`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-blue-500 hover:underline font-mono"
                  >
                    /c/{form.slug}
                  </a>
                  <ExternalLink className="w-3 h-3 text-blue-400" />
                </p>
              )}
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">Description</label>
              <RichTextEditor
                value={form.description}
                onChange={v => u("description", v)}
              />
            </div>

            {/* Category Image */}
            <ImageUploader
              label="Category Image"
              value={form.thumbnail_image}
              onChange={v => u("thumbnail_image", v)}
              hint="Recommended size: 500 x 500 px (White background)"
              aspectClass="aspect-square"
            />

            {/* Banner Image */}
            <ImageUploader
              label="Banner Image (Optional)"
              value={form.banner_image}
              onChange={v => u("banner_image", v)}
              hint="Recommended size: 1440 x 400 px (for website banner)"
              aspectClass="aspect-video"
            />

            {/* Status + Position */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Status</label>
                <div className="relative">
                  <select
                    value={form.status}
                    onChange={e => u("status", e.target.value)}
                    className="w-full text-sm border border-slate-200 rounded-xl px-3 py-2.5 focus:outline-none focus:border-blue-400 bg-white appearance-none pr-8"
                  >
                    <option value="active">Active</option>
                    <option value="hidden">Hidden</option>
                  </select>
                  <ChevronRight className="absolute right-3 top-1/2 -translate-y-1/2 rotate-90 w-4 h-4 text-slate-400 pointer-events-none" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Display Position</label>
                <input
                  type="number"
                  min={1}
                  value={form.position}
                  onChange={e => u("position", Number(e.target.value))}
                  className="w-full text-sm border border-slate-200 rounded-xl px-3 py-2.5 focus:outline-none focus:border-blue-400 bg-white"
                />
              </div>
            </div>
          </>
        )}

        {tab === "SEO" && (
          <>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">SEO Title</label>
              <input
                value={form.seo_title}
                onChange={e => u("seo_title", e.target.value)}
                placeholder="Same as name if empty"
                className="w-full text-sm border border-slate-200 rounded-xl px-3 py-2.5 focus:outline-none focus:border-blue-400"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">Meta Description</label>
              <textarea
                value={form.seo_description}
                onChange={e => u("seo_description", e.target.value)}
                rows={4}
                placeholder="150–160 chars for best SEO results..."
                className="w-full text-sm border border-slate-200 rounded-xl px-3 py-2.5 focus:outline-none focus:border-blue-400 resize-none"
              />
              <p className="text-xs text-slate-400 mt-1">{form.seo_description.length}/160</p>
            </div>
          </>
        )}

        {tab === "Display" && (
          <>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">Background Color</label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={form.bg_color || "#0f172a"}
                  onChange={e => u("bg_color", e.target.value)}
                  className="w-10 h-10 rounded-lg border cursor-pointer p-0.5"
                />
                <input
                  value={form.bg_color}
                  onChange={e => u("bg_color", e.target.value)}
                  placeholder="#0f172a"
                  className="flex-1 text-sm border border-slate-200 rounded-xl px-3 py-2.5 focus:outline-none focus:border-blue-400"
                />
              </div>
            </div>
            <div className="rounded-xl overflow-hidden h-24" style={{ background: form.bg_color || "#0f172a" }}>
              <div className="w-full h-full bg-gradient-to-r from-black/60 to-transparent flex items-end p-4">
                <p className="text-white text-sm font-bold">{form.name || "Category Preview"}</p>
              </div>
            </div>
          </>
        )}

        {tab === "Banner & Media" && (
          <>
            <ImageUploader
              label="Category Thumbnail"
              value={form.thumbnail_image}
              onChange={v => u("thumbnail_image", v)}
              hint="500 × 500 px, white background recommended"
              aspectClass="aspect-square"
            />
            <ImageUploader
              label="Category Banner"
              value={form.banner_image}
              onChange={v => u("banner_image", v)}
              hint="1440 × 400 px for full-width website banner"
              aspectClass="aspect-video"
            />
          </>
        )}
      </div>

      {/* Footer */}
      <div className="px-5 py-4 border-t border-slate-100 flex gap-3">
        <button
          onClick={onClose}
          className="flex-1 text-sm text-slate-600 font-semibold py-2.5 rounded-xl border border-slate-200 hover:bg-slate-50 transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-semibold py-2.5 rounded-xl flex items-center justify-center gap-2 transition-colors"
          data-testid="save-category-btn"
        >
          <Save className="w-4 h-4" />
          {saving ? "Saving..." : (isEdit ? "Update" : "Save Category")}
        </button>
      </div>
    </div>
  );
}

export default function CategoryBanners() {
  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState({ total_categories: 0, total_sub_categories: 0, total_products: 0, visible_on_website: 0 });
  const [loading, setLoading] = useState(true);
  const [selectedSlug, setSelectedSlug] = useState(null);
  const [search, setSearch] = useState("");
  const [showPanel, setShowPanel] = useState(false);
  const [editCat, setEditCat] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [catsRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/categories`, { headers: { Authorization: `Bearer ${getToken()}` } }),
        axios.get(`${API}/admin/categories/stats`, { headers: { Authorization: `Bearer ${getToken()}` } }).catch(() => ({ data: stats })),
      ]);
      setCategories(catsRes.data);
      if (statsRes.data?.total_categories !== undefined) setStats(statsRes.data);
    } catch { toast.error("Failed to load categories"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  const handleToggle = async (cat) => {
    const newStatus = cat.status === "active" ? "hidden" : "active";
    try {
      await axios.put(`${API}/admin/category/${cat.slug}`, { status: newStatus }, { headers: { Authorization: `Bearer ${getToken()}` } });
      setCategories(prev => prev.map(c => c.slug === cat.slug ? { ...c, status: newStatus } : c));
    } catch { toast.error("Failed"); }
  };

  const handleDelete = async (slug) => {
    try {
      await axios.delete(`${API}/admin/category/${slug}`, { headers: { Authorization: `Bearer ${getToken()}` } });
      toast.success("Deleted");
      setCategories(prev => prev.filter(c => c.slug !== slug));
      setConfirmDelete(null);
    } catch { toast.error("Failed to delete"); }
  };

  const topLevel = categories.filter(c => !c.parent_slug);

  const displayCats = categories.filter(c => {
    const inScope = !selectedSlug || selectedSlug === "__all__" || c.slug === selectedSlug || c.parent_slug === selectedSlug;
    const inSearch = !search || (c.name || c.title || "").toLowerCase().includes(search.toLowerCase());
    return inScope && inSearch;
  });

  const STAT_CARDS = [
    { label: "Total Categories", value: stats.total_categories || categories.length, color: "bg-blue-50 text-blue-600" },
    { label: "Sub Categories", value: stats.total_sub_categories, color: "bg-purple-50 text-purple-600" },
    { label: "Total Products", value: stats.total_products, color: "bg-green-50 text-green-600" },
    { label: "Visible on Site", value: stats.visible_on_website || categories.filter(c => c.status !== "hidden").length, color: "bg-orange-50 text-orange-600" },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-white border-b border-slate-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <nav className="flex items-center gap-1.5 text-xs text-slate-400 mb-1">
              <span>Admin</span><ChevronRight className="w-3 h-3" /><span className="text-slate-700 font-medium">Categories</span>
            </nav>
            <h1 className="text-xl font-black text-slate-900">Categories</h1>
            <p className="text-xs text-slate-500 mt-0.5">Manage product categories, sub-categories, banners and SEO</p>
          </div>
          <button onClick={() => { setEditCat(null); setShowPanel(true); }}
            className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold px-4 py-2 rounded-xl" data-testid="add-category-btn">
            <Plus className="w-4 h-4" /> Add Category
          </button>
        </div>
      </div>

      <div className="px-6 py-4 grid grid-cols-2 lg:grid-cols-4 gap-4">
        {STAT_CARDS.map(s => (
          <div key={s.label} className="bg-white rounded-xl border border-slate-100 px-4 py-4">
            <p className="text-2xl font-black text-slate-900">{Number(s.value).toLocaleString()}</p>
            <p className="text-xs text-slate-500 mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>

      <div className="flex gap-4 px-6 pb-6" style={{ height: "calc(100vh - 250px)", minHeight: 480 }}>
        <div className="w-52 bg-white border border-slate-200 rounded-xl flex flex-col flex-shrink-0">
          <div className="px-3 py-3 border-b">
            <p className="text-xs font-bold text-slate-700">Category Tree</p>
          </div>
          <div className="flex-1 overflow-y-auto p-2">
            <button onClick={() => setSelectedSlug("__all__")}
              className={`flex items-center gap-2 w-full text-left px-3 py-2 rounded-lg text-xs mb-1 transition-all ${!selectedSlug || selectedSlug === "__all__" ? "bg-orange-50 text-orange-600 font-semibold" : "text-slate-700 hover:bg-slate-50"}`}>
              <LayoutGrid className="w-3.5 h-3.5" />
              <span>All Categories</span>
              <span className="ml-auto text-[10px] bg-slate-100 text-slate-500 rounded px-1">{categories.length}</span>
            </button>
            {loading
              ? Array.from({ length: 5 }).map((_, i) => <div key={i} className="h-8 bg-slate-100 rounded-lg animate-pulse mb-1" />)
              : <CategoryTreeNode allCats={categories} selectedSlug={selectedSlug} onSelect={c => setSelectedSlug(c.slug)} />
            }
          </div>
        </div>

        <div className="flex-1 bg-white border border-slate-200 rounded-xl flex flex-col overflow-hidden">
          <div className="flex items-center gap-3 px-4 py-3 border-b">
            <div className="flex items-center gap-2 flex-1 bg-slate-50 rounded-lg px-3 py-1.5">
              <Search className="w-3.5 h-3.5 text-slate-400" />
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search categories..."
                className="text-xs bg-transparent outline-none flex-1" />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            <table className="w-full text-xs" data-testid="categories-table">
              <thead className="sticky top-0 bg-slate-50 border-b">
                <tr>
                  <th className="text-left px-4 py-2.5 font-semibold text-slate-500 w-8">#</th>
                  <th className="text-left px-4 py-2.5 font-semibold text-slate-500">Category</th>
                  <th className="text-left px-4 py-2.5 font-semibold text-slate-500">URL Slug</th>
                  <th className="text-left px-4 py-2.5 font-semibold text-slate-500">Products</th>
                  <th className="text-left px-4 py-2.5 font-semibold text-slate-500">Status</th>
                  <th className="text-left px-4 py-2.5 font-semibold text-slate-500">Position</th>
                  <th className="text-right px-4 py-2.5 font-semibold text-slate-500">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading
                  ? Array.from({ length: 6 }).map((_, i) => (
                    <tr key={i} className="border-b animate-pulse">
                      {Array.from({ length: 7 }).map((_, j) => <td key={j} className="px-4 py-3"><div className="h-3 bg-slate-100 rounded" /></td>)}
                    </tr>
                  ))
                  : displayCats.length === 0
                    ? <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">No categories found</td></tr>
                    : displayCats.map((cat, idx) => (
                      <tr key={cat.slug} className="border-b hover:bg-slate-50/50 transition-colors" data-testid={`cat-row-${cat.slug}`}>
                        <td className="px-4 py-3 text-slate-400">{idx + 1}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-lg bg-slate-100 flex-shrink-0 overflow-hidden flex items-center justify-center">
                              {cat.thumbnail_image
                                ? <img src={cat.thumbnail_image} alt="" className="w-full h-full object-cover" />
                                : <Tag className="w-3.5 h-3.5 text-slate-400" />}
                            </div>
                            <div>
                              <p className="font-semibold text-slate-800">{cat.name || cat.title}</p>
                              {cat.parent_slug && <p className="text-[10px] text-slate-400">Sub: {cat.parent_slug}</p>}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <a href={`/c/${cat.slug}`} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline flex items-center gap-1">
                            /c/{cat.slug} <ExternalLink className="w-2.5 h-2.5" />
                          </a>
                        </td>
                        <td className="px-4 py-3"><span className="bg-slate-100 text-slate-600 rounded px-2 py-0.5">{cat.product_count ?? "—"}</span></td>
                        <td className="px-4 py-3">
                          <button onClick={() => handleToggle(cat)}
                            className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold ${cat.status === "hidden" ? "bg-red-50 text-red-600 border border-red-200" : "bg-green-50 text-green-600 border border-green-200"}`}>
                            {cat.status === "hidden" ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                            {cat.status === "hidden" ? "Hidden" : "Active"}
                          </button>
                        </td>
                        <td className="px-4 py-3 text-slate-600 text-center">{cat.position ?? "—"}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1">
                            <button onClick={() => { setEditCat(cat); setShowPanel(true); }} className="p-1.5 rounded-lg text-slate-400 hover:text-orange-500 hover:bg-orange-50">
                              <Edit2 className="w-3.5 h-3.5" />
                            </button>
                            <button onClick={() => setConfirmDelete(cat.slug)} className="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50">
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                }
              </tbody>
            </table>
          </div>
          <div className="px-4 py-2 border-t text-xs text-slate-400">{displayCats.length} categories</div>
        </div>

        {showPanel && (
          <CatPanel cat={editCat} allCats={categories}
            onClose={() => { setShowPanel(false); setEditCat(null); }}
            onSaved={() => { setShowPanel(false); setEditCat(null); fetchAll(); }} />
        )}
      </div>

      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-80">
            <h3 className="text-base font-bold text-slate-900 mb-2">Delete Category?</h3>
            <p className="text-sm text-slate-500 mb-5">This removes the config only. Products are not affected.</p>
            <div className="flex gap-3">
              <button onClick={() => setConfirmDelete(null)} className="flex-1 text-sm text-slate-600 py-2 rounded-xl bg-slate-100">Cancel</button>
              <button onClick={() => handleDelete(confirmDelete)} className="flex-1 text-sm text-white py-2 rounded-xl bg-red-500 hover:bg-red-600">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
